import { useCallback, useEffect, useRef, useState } from "react";
import {
  parseAndroidMessage,
  sendAndroidMessage,
} from "../../services/androidBridge";

const parseValue = (value) =>
  typeof value === "string" ? JSON.parse(value) : value;

const normalizeAccounts = (data) => {
  const parsed = parseValue(data);
  const accounts = Array.isArray(parsed) ? parsed : parseValue(parsed?.data);

  return [...new Set((accounts ?? []).map((account) => account.accounts))].map(
    (account) => ({ label: account, value: account })
  );
};

const ReferencePayment = ({ service, context = {} }) => {
  const [selectedAccount, setSelectedAccount] = useState("");
  const [accountList, setAccountList] = useState([]);
  const [language, setLanguage] = useState(0);
  const [userName, setUserName] = useState("");
  const [reference, setReference] = useState("");
  const [queryInputValues, setQueryInputValues] = useState(() =>
    Object.fromEntries(
      (service.queryFields ?? []).map(({ key, initialValue = "" }) => [
        key,
        initialValue,
      ])
    )
  );
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("idle");

  const currentState = useRef({});
  currentState.current = {
    selectedAccount,
    userName,
    reference,
    detail,
    language,
    queryInputValues,
    ...context,
  };

  const labels = service.getLabels(language);
  const isLoading = status === "querying" || status === "paying";
  const isFormValid =
    selectedAccount !== "" &&
    reference.trim() !== "" &&
    (service.requiredContextKeys ?? []).every((key) => context[key]) &&
    (service.queryFields ?? []).every(({ key }) =>
      queryInputValues[key]?.trim()
    );

  const showLoading = useCallback(() => {
    sendAndroidMessage("openLoading", "openLoading", { data: true });
  }, []);

  const closeLoading = useCallback(() => {
    sendAndroidMessage("closeLoading", "closeLoading", { data: true });
  }, []);

  const showWarning = useCallback((message) => {
    sendAndroidMessage("warning", "warning", { data: message });
  }, []);

  const submitPayment = useCallback(() => {
    const state = currentState.current;
    const paymentRequest = service.paymentRequest(state);

    setStatus("paying");
    showLoading();
    sendAndroidMessage("requestPost", service.paymentEndpoint, {
      data: JSON.stringify(paymentRequest),
    });
  }, [service, showLoading]);

  const handleServiceResponse = useCallback(
    (url, responseData) => {
      const rawResponse = parseValue(responseData);
      const response = service.normalizeResponse
        ? service.normalizeResponse(rawResponse)
        : {
            success: rawResponse.status === true,
            payload: rawResponse.data?.payload,
            error: rawResponse.desc,
          };

      closeLoading();

      if (!response.success) {
        setStatus("error");
        showWarning(response.error);
        return;
      }

      if (url?.includes(service.queryEndpoint)) {
        const mappedDetail = service.mapQueryResponse(response.payload);
        setDetail(mappedDetail);

        if (service.autoConfirmQuery) {
          const state = { ...currentState.current, detail: mappedDetail };
          const currentLabels = service.getLabels(state.language);
          const message = service.confirmationMessage(
            mappedDetail,
            currentLabels,
            state.language,
            state
          );

          setStatus("confirming");
          sendAndroidMessage("confirm", "confirm", { data: message });
          return;
        }

        setStatus("review");
        return;
      }

      if (url?.includes(service.paymentEndpoint)) {
        const state = currentState.current;
        const message = service.successMessage({
          payment: service.mapPaymentResponse(response.payload),
          detail: state.detail,
          reference: state.reference,
        });

        setStatus("success");
        sendAndroidMessage("success", "success", { data: message });
      }
    },
    [closeLoading, service, showWarning]
  );

  const handleAndroidMessage = useCallback(
    (message) => {
      const incoming = parseAndroidMessage(message);

      switch (incoming.name) {
        case "accountList":
          setAccountList(normalizeAccounts(incoming.data));
          break;
        case "confirmResponse":
          if (incoming.data === false || incoming.data === "false") {
            setStatus("review");
          } else {
            submitPayment();
          }
          break;
        case "jwt":
          setLanguage(incoming.data);
          sendAndroidMessage("title", "title", {
            data: service.getTitle(incoming.data),
          });
          break;
        case "response":
          handleServiceResponse(incoming.url, incoming.data);
          break;
        case "userName":
          setUserName(incoming.data);
          break;
        default:
          break;
      }
    },
    [handleServiceResponse, service, submitPayment]
  );

  useEffect(() => {
    const previousReceiver = window.receiveDataFromAndroid;
    window.receiveDataFromAndroid = handleAndroidMessage;
    sendAndroidMessage("first", "first");

    return () => {
      if (window.receiveDataFromAndroid === handleAndroidMessage) {
        window.receiveDataFromAndroid = previousReceiver;
      }
    };
  }, [handleAndroidMessage]);

  const search = () => {
    setDetail(null);
    setStatus("querying");
    showLoading();
    const queryRequest = service.queryRequest(currentState.current);
    sendAndroidMessage("requestPost", service.queryEndpoint, {
      data: service.serializeQueryRequest
        ? JSON.stringify(queryRequest)
        : queryRequest,
    });
  };

  const requestConfirmation = () => {
    const message = service.confirmationMessage(
      detail,
      labels,
      language,
      currentState.current
    );
    setStatus("confirming");
    sendAndroidMessage("confirm", "confirm", { data: message });
  };

  const handleReferenceChange = (event) => {
    setReference(event.target.value);
    setDetail(null);
    setStatus("idle");
  };

  const handleQueryFieldChange = (key, value) => {
    setQueryInputValues((currentValues) => ({
      ...currentValues,
      [key]: value,
    }));
    setDetail(null);
    setStatus("idle");
  };

  return (
    <main className="service-screen w-full max-w-md mx-auto p-4">
      <section className="border border-gray-300 rounded-xl shadow-lg p-6 bg-white">
        <div className="mb-4">
          <label className="block mb-1 font-medium text-gray-700">
            {labels[service.accountLabelIndex ?? 0]}
          </label>
          <select
            value={selectedAccount}
            onChange={(event) => setSelectedAccount(event.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">{labels[service.accountLabelIndex ?? 0]}</option>
            {accountList.map((account) => (
              <option key={account.value} value={account.value}>
                {account.label}
              </option>
            ))}
          </select>
        </div>

        {(service.contextFields ?? []).map(({ key, labelIndex }) => (
          <div className="mb-4" key={key}>
            <label className="block mb-1 font-medium text-gray-700">
              {labels[labelIndex]}
            </label>
            <input
              type="text"
              value={context[key] ?? ""}
              readOnly
              className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-gray-600"
            />
          </div>
        ))}

        <div className="mb-4">
          <label className="block mb-1 font-medium text-gray-700">
            {labels[service.referenceLabelIndex ?? 1]}
          </label>
          <input
            type="text"
            value={reference}
            onChange={handleReferenceChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder={labels[service.referenceLabelIndex ?? 1]}
          />
        </div>

        {(service.queryFields ?? []).map(({ key, labelIndex, type = "text" }) => (
          <div className="mb-4" key={key}>
            <label className="block mb-1 font-medium text-gray-700">
              {labels[labelIndex]}
            </label>
            <input
              type={type}
              value={queryInputValues[key] ?? ""}
              onChange={(event) => handleQueryFieldChange(key, event.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder={labels[labelIndex]}
            />
          </div>
        ))}

        <button
          type="button"
          onClick={search}
          disabled={!isFormValid || isLoading}
          className={`w-full py-2 rounded text-white font-semibold transition ${
            isFormValid && !isLoading
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {service.getSearchLabel?.(language) ?? labels[service.searchLabelIndex ?? 2]}
        </button>

        {detail && !service.autoConfirmQuery && (
          <>
            <div className="bg-gray-100 p-4 mt-4 rounded">
              {service.detailFields.map(({ labelIndex, field }) => (
                <p key={field}>
                  <strong>{labels[labelIndex]}:</strong> {detail[field]}
                </p>
              ))}
            </div>
            <button
              type="button"
              onClick={requestConfirmation}
              disabled={isLoading}
              className="w-full py-2 mt-4 rounded text-white font-semibold transition bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {labels[service.continueLabelIndex ?? labels.length - 1]}
            </button>
          </>
        )}
      </section>
    </main>
  );
};

export default ReferencePayment;
